import { FC, useCallback, useEffect } from 'react';

import { useTranslations } from 'hooks/useTranslations';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useDeleteRegisteredProductMutation } from 'store/productService';
import { pushGTMEvent } from 'utils/analyticsUtils';
import { parseJsonError } from 'utils/fetchUtils';
import { showSuccessToast } from 'utils/toastUtils';
import { localizationSelector } from 'store/localizationState';
import * as routes from 'router/routes';

import { ModalDescription, ModalTitle } from 'styles';
import Button from 'components/UI/Buttons/Button';
import Loader from 'components/UI/Loader';
import Modal from 'components/UI/Modal';

import { ButtonGroup, DescriptionDisclaimer, LoaderWrapper } from './styled';
import { useNavigate } from 'react-router';

type Props = {
  isOpen: boolean;
  close: () => void;
  iprId: string;
  customerId: string;
};

const DeleteProductModal: FC<Props> = ({
  isOpen,
  close,
  iprId,
  customerId
}) => {
  const t = useTranslations();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { languageCode } = useAppSelector(localizationSelector);

  // Hooks
  const [deleteRegisteredProduct, { isSuccess, error, isLoading }] =
    useDeleteRegisteredProductMutation();

  // Handle delete registered product
  const onDeleteRegisteredProduct = useCallback(() => {
    if (iprId && languageCode) {
      deleteRegisteredProduct({
        iprId,
        languageCode
      });
    }
  }, [iprId, deleteRegisteredProduct, languageCode]);

  // Handle success
  useEffect(() => {
    if (isSuccess) {
      showSuccessToast(t('toast.product_delete_success'));

      // Track success event for analytics
      pushGTMEvent('deleteRegisteredProductSuccess', {
        elementInfo: {
          iprId,
          customerId
        }
      });
    }
  }, [dispatch, isSuccess, iprId, customerId, t]);

  // Handle error
  useEffect(() => {
    if (error) {
      const { data } = error as any;
      const apiError = parseJsonError(data);

      // Track error event for analytics
      pushGTMEvent('deleteRegisteredProductError', {
        elementInfo: {
          text: apiError.message,
          iprId,
          customerId
        }
      });
    }
  }, [dispatch, error, iprId, customerId]);

  // Close if complete
  useEffect(() => {
    if (isSuccess) {
      close();
      navigate(routes.PRODUCTS);
    }
  }, [close, isSuccess, navigate]);

  return (
    <Modal isOpen={isOpen} large close={close}>
      <ModalTitle>{t('delete_product.title')}</ModalTitle>
      <ModalDescription>{t('delete_product.description')}</ModalDescription>
      <DescriptionDisclaimer>
        {t('delete_product.disclaimer')}
      </DescriptionDisclaimer>
      {isLoading && (
        <LoaderWrapper>
          <Loader center />
        </LoaderWrapper>
      )}
      <ButtonGroup>
        <Button onClick={close} color="transparent" textColor="primary">
          {t('delete_product.cancel')}
        </Button>
        <Button
          onClick={onDeleteRegisteredProduct}
          submit
          disabled={isLoading}
          color="danger"
        >
          {t('delete_product.delete')}
        </Button>
      </ButtonGroup>
    </Modal>
  );
};

export default DeleteProductModal;
